import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Heart } from 'lucide-react';
import { images } from '../utils/images';

const Hero = () => {
  return (
    <section id="home" className="relative pt-20 overflow-hidden">
      <div className="relative z-10">
        <div className="container mx-auto px-4 py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-left space-y-6">
              <div className="inline-block">
                <span 
                  className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white shadow-sm"
                  style={{ color: '#587740' }}
                >
                  <Heart className="w-4 h-4 mr-2" style={{ color: '#81b956' }} />
                  Free Consultation & Action Plan
                </span>
              </div>
              
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight"
                style={{ color: '#587740' }}
              >
                You Deserve Support That Truly Cares
              </h1>
              
              <p 
                className="text-lg md:text-xl leading-relaxed"
                style={{ color: '#5e5e5e' }}
              >
                We're not just another NDIS provider. We're a team of real people who understand what it feels like to be overlooked, dismissed, or left to figure things out alone. We're here to change that — starting with you.
              </p>

              <p 
                className="text-base md:text-lg leading-relaxed"
                style={{ color: '#5e5e5e' }}
              >
                Individualised programs to reach your goals can look the same at various parts of the journey and unique at the end.
              </p>

              <p 
                className="text-base md:text-lg leading-relaxed italic"
                style={{ color: '#6c935e' }}
                data-testid="hero-tagline"
              >
                We see the psychological struggles that go unseen. You are heard and valued and in control.
              </p>

              <div 
                className="p-4 rounded-xl border-l-4"
                style={{ backgroundColor: '#81b95610', borderColor: '#81b956' }}
              >
                <p className="text-base font-semibold" style={{ color: '#587740' }}>
                  Book a free consultation — we'll review your situation and create a personalised action plan based on your current NDIS plan, at no cost to you.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button 
                  size="lg"
                  onClick={() => window.location.href = '/contact'}
                  className="text-white hover:opacity-90 transition-opacity text-base px-8 py-6"
                  style={{ backgroundColor: '#81b956' }}
                >
                  Get Your Free Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => window.location.href = '/get-on-ndis-free'}
                  className="border-2 hover:bg-opacity-10 transition-colors text-base px-8 py-6"
                  style={{ 
                    borderColor: '#81b956',
                    color: '#81b956'
                  }}
                >
                  Get on the NDIS Free
                </Button>
              </div>
            </div>

            {/* Right Content - Hero Video */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <video 
                  ref={(el) => {
                    if (el && !el._bounceBound) {
                      el._bounceBound = true;
                      let direction = 1;
                      const step = () => {
                        if (!el.duration) { requestAnimationFrame(step); return; }
                        if (direction === 1 && el.currentTime >= el.duration - 0.05) {
                          direction = -1;
                        }
                        if (direction === -1) {
                          el.currentTime = Math.max(0, el.currentTime - 0.03);
                          if (el.currentTime <= 0.05) {
                            direction = 1;
                            el.currentTime = 0;
                            el.play();
                          }
                        }
                        requestAnimationFrame(step);
                      };
                      el.addEventListener('play', () => { direction = 1; requestAnimationFrame(step); }, { once: true });
                    }
                  }}
                  src="https://customer-assets.emergentagent.com/job_fa8adf7f-dc99-4cbb-b0a0-483513240e89/artifacts/xq5309an_NDIS%20Support%20Coordinator%20Hero%20Video.mp4"
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-[400px] md:h-[500px] object-cover"
                  data-testid="hero-video"
                />
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-start space-x-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: '#AABC5A' }}
                  >
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg" style={{ color: '#587740' }}>
                      Free Action Plan
                    </h3>
                    <p className="text-sm" style={{ color: '#5e5e5e' }}>
                      Personalised support that puts you first
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-1/3 h-full opacity-5 pointer-events-none">
        <div 
          className="absolute top-20 right-20 w-64 h-64 rounded-full blur-3xl"
          style={{ backgroundColor: '#AABC5A' }}
        />
      </div>
    </section>
  );
};

export default Hero;
